import { ERRORS } from '@/common/constants/errors';
import { MARKET, TMarket } from '@/common/constants/markets';
import { ICard } from '@/common/integrations/menhir/employee/card/card.types';
import { IEmployeePaymentMethod } from '@/common/integrations/menhir/employee/payments/payments.types';
import { capitalize } from '@coverflex-tech/hypernova-utils';

interface ICardColors {
  background?: string;
  icon?: string;
  letters?: string;
  data?: string;
}

const ISO8601DateRegex = /^\d{4}-\d{2}-\d{2}/;

export const getCardColorsByStatus = (
  status?: ICard['status']
): ICardColors => {
  if (status === 'locked') {
    return {
      background: '#E8F6FB',
      icon: '#DCEBF1',
      letters: '#95C8DA4D',
      data: '#368CB8',
    };
  }

  if (status === 'cancelled' || status === 'expired') {
    return {
      background: '#EBEEF2',
      icon: '#DDE0E3',
      letters: '#DDE0E3',
      data: '#5F7287',
    };
  }

  return {
    background: '#FF9878',
    icon: '#FFFFFF',
    letters: '#FFFFFF',
    data: '#FFFFFF',
  };
};

export const getFormattedExpDate = (card?: ICard) => {
  if (!card?.expirationDate) return '••/••';

  if (ISO8601DateRegex.test(card.expirationDate)) {
    const [year, month] = card.expirationDate.split('-');
    return `${month}/${year?.slice(2)}`;
  }
};

export type TCardConfiguration = {
  requiresDisplayName: boolean;
  requiresPIN: boolean;
  requiresDistrict: boolean;
  restrictSpecialCharacters: boolean;
  maxAllowedCharacters?: number;
  isAbleToChangePin: boolean;
};

export const useCardConfigurationMarket = (
  market: TMarket
): TCardConfiguration => {
  if (market === MARKET.PT) {
    return {
      requiresDisplayName: true,
      requiresPIN: false,
      requiresDistrict: false,
      restrictSpecialCharacters: false,
      maxAllowedCharacters: undefined,
      isAbleToChangePin: false,
    };
  }
  if (market === MARKET.IT) {
    return {
      requiresDisplayName: true,
      requiresPIN: true,
      requiresDistrict: true,
      restrictSpecialCharacters: true,
      maxAllowedCharacters: 40,
      isAbleToChangePin: true,
    };
  }

  if (market === MARKET.ES) {
    return {
      requiresDisplayName: true,
      requiresPIN: true,
      requiresDistrict: true,
      restrictSpecialCharacters: true,
      maxAllowedCharacters: 40,
      isAbleToChangePin: true,
    };
  }

  throw new Error(ERRORS.MARKET_NOT_SUPPORTED);
};

export const cardToString = (
  card: IEmployeePaymentMethod | null | undefined
) => {
  if (!card) {
    return '';
  }

  const cardBrand =
    card.metadata.brand && card.metadata.brand !== 'unknown'
      ? `${capitalize(card?.metadata.brand)} `
      : '';

  return `${cardBrand}•••• ${card?.metadata.last4Digits}`;
};
